import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, ShoppingBag, Sparkles, Truck, Store } from "lucide-react";
import { Button, Input, Label, Skeleton, Textarea } from "@/components/ui";
import { formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

// ─── Shape of /api/public/shop ─────────────────────────────────────────────
interface ShopProduct {
  id: string; name: string; description: string | null; image_url: string | null;
  price_cents: number; sold_out: boolean; few_left: number | null; max_qty: number;
}
interface ShopMenu {
  business: { name: string; logo_url: string | null };
  products: ShopProduct[];
  /** null = pick up at the clinic only. */
  shipping_cents: number | null;
  tax_rate_pct: number;
}

const BASKET_KEY = "hh.shop.basket";
// The basket as it was when they went to pay, so backing out restores it.
const PENDING_KEY = "hh.shop.pending";

function readBasket(): Record<string, number> {
  try {
    if (new URLSearchParams(window.location.search).get("cancelled") === "1") {
      const pending = window.localStorage.getItem(PENDING_KEY);
      if (pending) {
        window.localStorage.setItem(BASKET_KEY, pending);
        window.localStorage.removeItem(PENDING_KEY);
      }
    }
    const raw = window.localStorage.getItem(BASKET_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed as Record<string, number> : {};
  } catch { return {}; }
}
function writeBasket(b: Record<string, number>) {
  try { window.localStorage.setItem(BASKET_KEY, JSON.stringify(b)); } catch { /* private window */ }
}

/**
 * The public web shop: "We also have products to sell, can we do them
 * online?" Clients pick products, choose pick-up (or shipping when she
 * offers it) and pay on Stripe's secure page. The order lands in the app as
 * a paid sale, the stock count goes down, and the front desk gets a task.
 *
 * Mounted at /shop as a public, no-login route (like /booking).
 */
export function ShopPage() {
  const [menu, setMenu] = useState<ShopMenu | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [basket, setBasket] = useState<Record<string, number>>(() => readBasket());
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [delivery, setDelivery] = useState<"pickup" | "ship">("pickup");
  const [submitting, setSubmitting] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const cancelled = useMemo(() => new URLSearchParams(window.location.search).get("cancelled") === "1", []);

  useEffect(() => {
    fetch("/api/public/shop")
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "The shop is unavailable right now");
        return r.json() as Promise<ShopMenu>;
      })
      .then(setMenu)
      .catch((e) => setError(e instanceof Error ? e.message : "The shop is unavailable right now"));
  }, []);

  useEffect(() => { writeBasket(basket); }, [basket]);

  const lines = useMemo(() => {
    if (!menu) return [];
    return menu.products
      .filter((p) => (basket[p.id] ?? 0) > 0 && !p.sold_out)
      .map((p) => ({ product: p, qty: Math.min(basket[p.id]!, p.max_qty) }))
      .filter((l) => l.qty > 0);
  }, [menu, basket]);

  const subtotal = lines.reduce((s, l) => s + l.product.price_cents * l.qty, 0);
  const tax = menu ? Math.round((subtotal * menu.tax_rate_pct) / 100) : 0;
  const shipping = delivery === "ship" && menu?.shipping_cents != null ? menu.shipping_cents : 0;
  const total = subtotal + tax + shipping;

  const setQty = (p: ShopProduct, qty: number) => {
    setBasket((b) => {
      const next = { ...b };
      const q = Math.max(0, Math.min(qty, p.max_qty));
      if (q === 0) delete next[p.id]; else next[p.id] = q;
      return next;
    });
  };

  const placeOrder = async () => {
    if (lines.length === 0 || !name.trim() || !email.trim()) return;
    setSubmitting(true);
    setOrderError(null);
    try {
      const r = await fetch("/api/public/shop/order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: lines.map((l) => ({ product_id: l.product.id, qty: l.qty })),
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          delivery,
          notes: notes.trim() || undefined,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.url) throw new Error(d.error || "Something went wrong. Please try again.");
      // The basket empties on the way to the payment page; if they back
      // out, Stripe returns them here with ?cancelled=1 and it's refilled
      // from what they had.
      try {
        window.localStorage.setItem(PENDING_KEY, JSON.stringify(basket));
        window.localStorage.removeItem(BASKET_KEY);
      } catch { /* private window */ }
      window.location.href = d.url as string;
    } catch (e) {
      setOrderError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-zinc-200">Shop unavailable</h1>
          <p className="mt-2 text-sm text-zinc-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="min-h-screen bg-zinc-950 px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto max-w-5xl"><Skeleton className="h-96 rounded-2xl" /></div>
      </div>
    );
  }

  const offersShipping = menu.shipping_cents !== null;

  return (
    <div className="min-h-screen bg-zinc-950 px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6 flex items-center gap-3">
          {menu.business.logo_url ? (
            <img src={menu.business.logo_url} alt={menu.business.name} className="h-11 w-11 rounded-xl object-cover ring-1 ring-zinc-800" />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-fuchsia-500/15">
              <Sparkles className="h-5 w-5 text-fuchsia-300" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-semibold text-zinc-100">{menu.business.name}</h1>
            <p className="text-sm text-zinc-500">Shop our products. Pick up at the clinic{offersShipping ? " or have them shipped" : ""}.</p>
          </div>
        </header>

        {cancelled && (
          <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            Payment wasn&rsquo;t completed, so nothing was charged. Your basket is still here.
          </p>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_22rem]">
          {/* Products */}
          <section>
            {menu.products.length === 0 ? (
              <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 p-10 text-center">
                <ShoppingBag className="mx-auto h-6 w-6 text-zinc-500" />
                <p className="mt-2 text-sm text-zinc-400">Nothing is for sale online right now. Please check back soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {menu.products.map((p) => {
                  const qty = basket[p.id] ?? 0;
                  return (
                    <article key={p.id} className="flex flex-col overflow-hidden rounded-2xl border border-zinc-800/70 bg-zinc-900/30">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.name} className="aspect-square w-full object-cover" loading="lazy" />
                      ) : (
                        <div className="flex aspect-square w-full items-center justify-center bg-zinc-900">
                          <ShoppingBag className="h-8 w-8 text-zinc-700" />
                        </div>
                      )}
                      <div className="flex flex-1 flex-col p-4">
                        <div className="flex items-start justify-between gap-3">
                          <h2 className="text-sm font-semibold text-zinc-100">{p.name}</h2>
                          <span className="shrink-0 text-sm font-semibold tabular-nums text-zinc-200">{formatMoney(p.price_cents)}</span>
                        </div>
                        {p.description && <p className="mt-1 text-xs leading-relaxed text-zinc-500">{p.description}</p>}
                        <div className="mt-auto pt-3">
                          {p.sold_out ? (
                            <p className="text-xs font-medium text-zinc-500">Sold out</p>
                          ) : qty === 0 ? (
                            <Button size="sm" className="w-full" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setQty(p, 1)}>
                              Add to basket
                            </Button>
                          ) : (
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1">
                                <button type="button" aria-label={`One less ${p.name}`} onClick={() => setQty(p, qty - 1)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 text-zinc-300 hover:border-zinc-600">
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span className="w-8 text-center text-sm tabular-nums text-zinc-100">{qty}</span>
                                <button type="button" aria-label={`One more ${p.name}`} disabled={qty >= p.max_qty} onClick={() => setQty(p, qty + 1)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 text-zinc-300 hover:border-zinc-600 disabled:opacity-40">
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              <span className="text-xs text-zinc-500">In your basket</span>
                            </div>
                          )}
                          {!p.sold_out && p.few_left !== null && (
                            <p className="mt-1.5 text-[11px] text-amber-300">Only {p.few_left} left</p>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {/* Basket + checkout */}
          <aside className="h-fit rounded-2xl border border-zinc-800/70 bg-zinc-900/30 p-5 lg:sticky lg:top-6">
            <h2 className="text-base font-semibold text-zinc-100">Your basket</h2>
            {lines.length === 0 ? (
              <p className="mt-2 text-sm text-zinc-500">Nothing here yet.</p>
            ) : (
              <>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {lines.map((l) => (
                    <li key={l.product.id} className="flex justify-between gap-2 text-zinc-300">
                      <span className="min-w-0 truncate">{l.qty} x {l.product.name}</span>
                      <span className="shrink-0 tabular-nums">{formatMoney(l.product.price_cents * l.qty)}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 space-y-2">
                  <Label>How would you like it?</Label>
                  <div className="grid grid-cols-1 gap-2">
                    <DeliveryOption active={delivery === "pickup"} onClick={() => setDelivery("pickup")}
                      icon={<Store className="h-4 w-4" />} title="Pick up at the clinic" sub="Free. We'll have it ready for you." />
                    {offersShipping && (
                      <DeliveryOption active={delivery === "ship"} onClick={() => setDelivery("ship")}
                        icon={<Truck className="h-4 w-4" />} title="Ship it to me"
                        sub={`${formatMoney(menu.shipping_cents ?? 0)}. You'll enter the address on the payment page.`} />
                    )}
                  </div>
                </div>

                <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); void placeOrder(); }}>
                  <div>
                    <Label required>Your name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                  </div>
                  <div>
                    <Label required>Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                    <p className="mt-1 text-[11px] text-zinc-500">Your receipt goes here.</p>
                  </div>
                  <div>
                    <Label>Phone (optional)</Label>
                    <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
                    <p className="mt-1 text-[11px] text-zinc-500">Only in case we need to reach you about this order.</p>
                  </div>
                  <div>
                    <Label>Anything we should know? (optional)</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                  </div>

                  <div className="space-y-1 border-t border-zinc-800 pt-3 text-sm">
                    <Row label="Subtotal" cents={subtotal} />
                    {tax > 0 && <Row label="Sales tax" cents={tax} />}
                    {shipping > 0 && <Row label="Shipping" cents={shipping} />}
                    <div className="flex justify-between pt-1 font-semibold text-zinc-100">
                      <span>Total</span><span className="tabular-nums">{formatMoney(total)}</span>
                    </div>
                  </div>

                  {orderError && (
                    <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{orderError}</p>
                  )}
                  <Button type="submit" size="lg" className="w-full" loading={submitting}
                    disabled={!name.trim() || !email.trim() || lines.length === 0}>
                    Pay {formatMoney(total)} securely
                  </Button>
                  <p className="text-center text-[11px] text-zinc-500">You&rsquo;ll pay on Stripe&rsquo;s secure card page.</p>
                </form>
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ label, cents }: { label: string; cents: number }) {
  return (
    <div className="flex justify-between text-zinc-400">
      <span>{label}</span><span className="tabular-nums">{formatMoney(cents)}</span>
    </div>
  );
}

function DeliveryOption({ active, onClick, icon, title, sub }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; title: string; sub: string;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left",
        active ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-zinc-800 hover:border-zinc-600",
      )}>
      <span className={cn("mt-0.5", active ? "text-fuchsia-300" : "text-zinc-500")}>{icon}</span>
      <span>
        <span className="block text-sm font-medium text-zinc-100">{title}</span>
        <span className="block text-xs text-zinc-500">{sub}</span>
      </span>
    </button>
  );
}
